import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { evalApi } from '@/services/evalApi';

interface CreateScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  evalId?: string;
  evalName?: string;
  onSuccess?: () => void;
}

type FrequencyType = 'minutes' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';

// Days of week for weekly selector
const DAYS_OF_WEEK = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

// Generate cron expression from frequency settings
function generateCron(
  frequency: FrequencyType,
  minute: string,
  hour: string,
  dayOfWeek: string,
  dayOfMonth: string,
  customCron: string
): string {
  switch (frequency) {
    case 'hourly':
      return `${minute} * * * *`;
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekly':
      return `${minute} ${hour} * * ${dayOfWeek}`;
    case 'monthly':
      return `${minute} ${hour} ${dayOfMonth} * *`;
    case 'custom':
      return customCron;
    default:
      return '';
  }
}

// Generate human-readable description
function describeSchedule(
  frequency: FrequencyType,
  intervalMinutes: string,
  minute: string,
  hour: string,
  dayOfWeek: string,
  dayOfMonth: string
): string {
  const time = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  const dayName = DAYS_OF_WEEK.find(d => d.value === dayOfWeek)?.label || dayOfWeek;

  switch (frequency) {
    case 'minutes':
      return `Every ${intervalMinutes} minute${intervalMinutes === '1' ? '' : 's'}`;
    case 'hourly':
      return `Every hour at :${minute.padStart(2, '0')}`;
    case 'daily':
      return `Daily at ${time}`;
    case 'weekly':
      return `Every ${dayName} at ${time}`;
    case 'monthly':
      return `Monthly on day ${dayOfMonth} at ${time}`;
    case 'custom':
      return 'Custom schedule';
    default:
      return '';
  }
}

interface EvalOption {
  id: string;
  name: string;
}

export function CreateScheduleDialog({
  open,
  onClose,
  evalId: propEvalId,
  evalName,
  onSuccess,
}: CreateScheduleDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [evals, setEvals] = useState<EvalOption[]>([]);
  const [loadingEvals, setLoadingEvals] = useState(false);

  // Form state
  const [evalId, setEvalId] = useState(propEvalId || '');
  const [frequency, setFrequency] = useState<FrequencyType>('daily');
  const [intervalMinutes, setIntervalMinutes] = useState('60');
  const [minute, setMinute] = useState('0');
  const [hour, setHour] = useState('9');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [customCron, setCustomCron] = useState('0 9 * * *');
  const [trialsPerRun, setTrialsPerRun] = useState('10');
  const [dateOverrideMode, setDateOverrideMode] = useState('none');
  const [dateOverrideValue, setDateOverrideValue] = useState('');

  // Generate schedule description
  const scheduleDescription = useMemo(() => {
    return describeSchedule(frequency, intervalMinutes, minute, hour, dayOfWeek, dayOfMonth);
  }, [frequency, intervalMinutes, minute, hour, dayOfWeek, dayOfMonth]);

  useEffect(() => {
    if (open) {
      if (!propEvalId) {
        loadEvals();
      }
      setError(null);
      // Reset form but keep propEvalId if provided
      setEvalId(propEvalId || '');
      setFrequency('daily');
      setIntervalMinutes('60');
      setMinute('0');
      setHour('9');
      setDayOfWeek('1');
      setDayOfMonth('1');
      setCustomCron('0 9 * * *');
      setTrialsPerRun('10');
      setDateOverrideMode('none');
      setDateOverrideValue('');
    }
  }, [open, propEvalId]);

  const loadEvals = async () => {
    setLoadingEvals(true);
    try {
      const data = await evalApi.listEvals();
      setEvals(data);
    } catch (err) {
      console.error('Failed to load evals:', err);
    } finally {
      setLoadingEvals(false);
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = {
        eval_id: evalId,
        trials_per_run: parseInt(trialsPerRun, 10),
        date_override_mode: dateOverrideMode,
        date_override_value: dateOverrideValue || null,
      };

      if (frequency === 'minutes') {
        payload.interval_minutes = parseInt(intervalMinutes, 10);
      } else {
        payload.cron_expression = generateCron(frequency, minute, hour, dayOfWeek, dayOfMonth, customCron);
      }

      await evalApi.createSchedule(payload);

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create schedule');
    } finally {
      setLoading(false);
    }
  };

  const isValid = evalId && (
    (frequency === 'minutes' && parseInt(intervalMinutes, 10) > 0) ||
    (frequency === 'custom' && customCron.trim()) ||
    (frequency !== 'minutes' && frequency !== 'custom')
  );

  // Generate hour options (0-23)
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: String(i),
    label: `${String(i).padStart(2, '0')}:00`,
  }));

  // Generate minute options (0, 15, 30, 45)
  const minuteOptions = [
    { value: '0', label: ':00' },
    { value: '15', label: ':15' },
    { value: '30', label: ':30' },
    { value: '45', label: ':45' },
  ];

  // Generate day of month options (1-28)
  const dayOfMonthOptions = Array.from({ length: 28 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Schedule Eval Run</DialogTitle>
          <DialogDescription>Set up automatic evaluation execution on a schedule.</DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-4 py-4">
          {/* Eval Selector */}
          {propEvalId ? (
            <div className="grid gap-2">
              <Label>Evaluation</Label>
              <Input value={evalName || propEvalId} disabled />
            </div>
          ) : (
            <div className="grid gap-2">
              <Label>Evaluation</Label>
              <Select value={evalId} onValueChange={setEvalId} disabled={loadingEvals}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an evaluation" />
                </SelectTrigger>
                <SelectContent>
                  {evals.map((ev) => (
                    <SelectItem key={ev.id} value={ev.id}>
                      {ev.name} ({ev.id})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Separator />

          {/* Frequency Selector */}
          <div className="grid gap-2">
            <Label>Frequency</Label>
            <Select value={frequency} onValueChange={(v) => setFrequency(v as FrequencyType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="minutes">Every X minutes</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="custom">Custom cron</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Frequency-specific inputs */}
          {frequency === 'minutes' && (
            <div className="grid gap-2">
              <Label>Run every</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={intervalMinutes}
                  onChange={(e) => setIntervalMinutes(e.target.value)}
                  min={1}
                  max={1440}
                  className="w-24"
                />
                <span className="text-sm text-muted-foreground">minutes</span>
              </div>
            </div>
          )}

          {frequency === 'hourly' && (
            <div className="grid gap-2">
              <Label>At minute</Label>
              <Select value={minute} onValueChange={setMinute}>
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {minuteOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {frequency === 'daily' && (
            <div className="grid gap-2">
              <Label>Time</Label>
              <div className="flex items-center gap-2">
                <Select value={hour} onValueChange={setHour}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {hourOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={minute} onValueChange={setMinute}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {minuteOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {frequency === 'weekly' && (
            <div className="grid gap-3">
              <div className="grid gap-2">
                <Label>Day</Label>
                <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={day.value}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Time</Label>
                <div className="flex items-center gap-2">
                  <Select value={hour} onValueChange={setHour}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hourOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={minute} onValueChange={setMinute}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {minuteOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {frequency === 'monthly' && (
            <div className="grid gap-3">
              <div className="grid gap-2">
                <Label>Day of month</Label>
                <Select value={dayOfMonth} onValueChange={setDayOfMonth}>
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {dayOfMonthOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Time</Label>
                <div className="flex items-center gap-2">
                  <Select value={hour} onValueChange={setHour}>
                    <SelectTrigger className="w-28">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {hourOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={minute} onValueChange={setMinute}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {minuteOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {frequency === 'custom' && (
            <div className="grid gap-2">
              <Label>Cron expression</Label>
              <Input
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
                className="font-mono"
                placeholder="0 9 * * 1-5"
              />
              <p className="text-xs text-muted-foreground">
                Format: minute hour day month weekday
              </p>
            </div>
          )}

          {/* Schedule preview */}
          {frequency !== 'custom' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
              <Clock className="h-4 w-4" />
              <span>{scheduleDescription}</span>
              <span className="text-xs">(± up to 5 min jitter)</span>
            </div>
          )}

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            {/* Trials per run */}
            <div className="grid gap-2">
              <Label>Trials per Run</Label>
              <Input
                type="number"
                value={trialsPerRun}
                onChange={(e) => setTrialsPerRun(e.target.value)}
                min={1}
                max={100}
              />
            </div>

            {/* Date Override Mode */}
            <div className="grid gap-2">
              <Label>Date Override</Label>
              <Select value={dateOverrideMode} onValueChange={setDateOverrideMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (use current)</SelectItem>
                  <SelectItem value="fixed">Fixed Date</SelectItem>
                  <SelectItem value="relative">Relative (days ago)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date Override Value */}
          {dateOverrideMode !== 'none' && (
            <div className="grid gap-2">
              <Label>{dateOverrideMode === 'fixed' ? 'Fixed Date' : 'Days Ago'}</Label>
              <Input
                type={dateOverrideMode === 'fixed' ? 'date' : 'number'}
                value={dateOverrideValue}
                onChange={(e) => setDateOverrideValue(e.target.value)}
                min={dateOverrideMode === 'relative' ? 0 : undefined}
              />
              <p className="text-sm text-muted-foreground">
                {dateOverrideMode === 'fixed'
                  ? 'All date-related tool calls will use this date'
                  : '0 = today, 1 = yesterday, etc.'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={loading || !isValid}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Create Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateScheduleDialog;
